<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"
  import { mdiEyeOutline as tablePrimaryIcon } from "@mdi/js"

  import { onDestroy, onMount } from "svelte"
  import Table, {
    type DataRow,
    type TableFilterValues,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import {
    archiveCustomersBatch,
    fetchCustomerTable,
    restoreCustomersBatch,
  } from "../../../../../../../../api/kehrblatt/customer-registry/customers"
  import { type RegistryArchiveBatchResponse } from "../../../../../../../../api/kehrblatt/archive"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { spaNavigate } from "@kontextwerk/web-sdk/core"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import {
    registerEventCallback,
    unregisterEventCallback,
  } from "../../../../../../../_shared/_logic/eventbus"
  import {
    readFlatTableFilterUrlState,
    writeFlatTableFilterUrlState,
  } from "../../../../_shared/_logic/table-filter-url-state"
  import type { OrgaViewFrameContext } from "../../../../../../context"
  import type { PageContext } from "../../../../_shared/_logic/customer-registry-index"
  import CustomerBatchActions from "./_modules/customer-batch-actions"
  import CustomerRowActions from "./_actions/CustomerRowActions.actions"
  import CustomerContactCell from "./_modules/customer-contact-cell"
  import CustomerIdentityCell from "./_modules/customer-identity-cell"
  import CustomerObjectCell from "./_modules/customer-object-cell"
  import CustomerRoleCell from "./_modules/customer-role-cell"
  import CustomersTableFilterBar from "./_modules/customers-table-filter-bar"
  import CustomersTableHeader from "./_modules/customers-table-header"
  import {
    customerRegistryContactsInvalidationTarget,
    invalidateCustomerRegistryContacts,
  } from "../../../../_shared/_logic/customer-registry-invalidation"
  import type {
    CustomerFilterValue,
    CustomerRow,
    FilterDisplayMode,
  } from "./_shared/_logic/customers-table-contract"
  import { customerTableColumns } from "./_logic/columns"
  import {
    buildCustomerFilterRefreshKey,
    createDefaultCustomerFilters,
  } from "./_shared/_logic/filters"
  import { buildCustomerRows } from "./_logic/tableModel"

  const { viewFrameContext: _viewFrameContext, pageContext } = $props<{
    viewFrameContext: OrgaViewFrameContext
    pageContext: PageContext
  }>()

  const PAGE_SIZE = 80

  const organization = $derived(pageContext.organization)
  const canEdit = $derived(pageContext.canEditCustomers)
  let refreshTable: (() => void | Promise<void>) | null = null
  let mutatingIds = $state<string[]>([])
  let batchMutating = $state(false)
  const numberFormatter = new Intl.NumberFormat("de-DE")
  let totalCount = $state(0)
  const titleWithCount = $derived.by(
    () => `Ansprechpartner (${numberFormatter.format(totalCount)})`,
  )
  const tableInvalidationTargets = $derived(
    organization?.id ? [customerRegistryContactsInvalidationTarget(organization.id)] : [],
  )

  const resolveCustomerTableUrlStateId = () =>
    organization?.id ? `kehrblatt-customers-${organization.id}` : "kehrblatt-customers"
  const customerTableUrlStateId = $derived.by(resolveCustomerTableUrlStateId)
  const readInitialUrlState = () =>
    readFlatTableFilterUrlState(resolveCustomerTableUrlStateId(), [
      "query",
      "customerType",
      "contactData",
      "propertyId",
      "role",
      "archiveScope",
    ])
  const initialUrlState = readInitialUrlState()
  const initialArchiveScope: ArchiveScope =
    initialUrlState.filters.archiveScope === "ARCHIVED" ||
    initialUrlState.filters.archiveScope === "ALL"
      ? initialUrlState.filters.archiveScope
      : "ACTIVE"

  let filters = $state<CustomerFilterValue>({
    ...createDefaultCustomerFilters(),
    ...initialUrlState.filters,
    archiveScope: initialArchiveScope,
  })
  const customerFilterRefreshKey = $derived.by(() => buildCustomerFilterRefreshKey(filters))
  let filterMode = $state<FilterDisplayMode>(
    initialUrlState.mode === "detailed" ? "detailed" : "compact",
  )

  const getRows: TableGetRows<TableFilterValues> = async (params) => {
    if (!organization?.id) return { rows: [], total: 0 }

    const search = filters.query.trim()
    const customerType = filters.customerType.trim()
    const contactData = filters.contactData.trim()
    const propertyId = filters.propertyId.trim()
    const role = filters.role.trim()
    const pageSize = params.limit && params.limit > 0 ? params.limit : undefined
    const sort = (params as { sort?: { key?: string | null; dir?: "asc" | "desc" | null } | null })
      .sort
    try {
      const response = await fetchCustomerTable({
        organizationId: organization.id,
        query: search || null,
        customerType: (customerType || null) as CustomerType | null,
        contactData: contactData || null,
        propertyId: propertyId || null,
        role: role || null,
        archiveScope: filters.archiveScope,
        sortBy: sort?.key ?? null,
        sortDir: sort?.dir ?? null,
        limit: pageSize,
        offset: params.offset,
      })
      const rows = buildCustomerRows({
        customers: response.items,
        propertyLinks: response.property_customers ?? [],
        properties: response.properties ?? [],
        contactPoints: response.contact_points ?? [],
      })

      return {
        rows,
        total: response.total ?? response.items.length,
      }
    } catch (err) {
      console.error("customers_load_failed", err)
      newNotification({
        class: "error",
        html: "Ansprechpartner konnten nicht geladen werden. Bitte Verbindung prüfen und erneut versuchen.",
      })
      throw err
    }
  }

  const handleFilterChange = () => {
    writeFlatTableFilterUrlState(
      customerTableUrlStateId,
      { filters: { ...filters }, mode: filterMode },
      { filters: createDefaultCustomerFilters(), mode: "compact" },
    )
  }

  const handleFilterModeChange = (mode: FilterDisplayMode) => {
    filterMode = mode === "detailed" ? "detailed" : "compact"
    handleFilterChange()
  }

  const openCustomer = (row: DataRow) => {
    const id = (row.id as string | undefined) ?? (row as CustomerRow)?.customer?.id
    const orgSlug = organization?.slug ?? null

    if (!id || !orgSlug) return

    spaNavigate(`/organizations/${orgSlug}/customers/${id}/details`, { noSearchParam: false })
  }

  const mutateCustomer = async (row: CustomerRow | DataRow) => {
    const id = (row.id as string | undefined) ?? (row as CustomerRow)?.customer?.id

    if (!id) return

    await mutateCustomers([id], (row as CustomerRow).customer?.archived_at ? "restore" : "archive")
  }

  const selectedCustomerIds = (rows: DataRow[]) =>
    Array.from(
      new Set(
        rows
          .map((row) => (row.id as string | undefined) ?? (row as CustomerRow)?.customer?.id)
          .filter((id): id is string => Boolean(id)),
      ),
    )

  const refreshContactsTable = async () => {
    if (!organization?.id) return

    await invalidateCustomerRegistryContacts(organization.id)
  }

  type ArchiveMode = "archive" | "restore"
  const customerResultMessage = (response: RegistryArchiveBatchResponse, mode: ArchiveMode) =>
    `${response.processed_count} Ansprechpartner ${mode === "archive" ? "archiviert" : "wiederhergestellt"}.`

  const mutateCustomers = async (ids: string[], mode: ArchiveMode, clearSelection?: () => void) => {
    if (!canEdit || batchMutating || !ids.length) return

    await newApproval({
      title:
        mode === "archive" ? "Ansprechpartner archivieren" : "Ansprechpartner wiederherstellen",
      description:
        mode === "archive"
          ? "Die ausgewählten Ansprechpartner aus neuen Suchen und Zuordnungen ausblenden? Bestehende Aufträge, Kontaktpunkte und Zuordnungen bleiben erhalten."
          : "Die ausgewählten Ansprechpartner wieder für neue Suchen und Zuordnungen freigeben?",
      approvalLabel: mode === "archive" ? "Ja, archivieren" : "Ja, wiederherstellen",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        batchMutating = true
        mutatingIds = ids
        try {
          const response =
            mode === "archive" ? await archiveCustomersBatch(ids) : await restoreCustomersBatch(ids)
          clearSelection?.()
          await refreshContactsTable()
          newNotification({
            class: response.failed_count ? "warning" : "success",
            html: customerResultMessage(response, mode),
          })
        } catch (err) {
          console.error(`customer_batch_${mode}_failed`, err)
          newNotification({
            class: "error",
            html: `Ansprechpartner konnten nicht ${mode === "archive" ? "archiviert" : "wiederhergestellt"} werden.`,
          })
        } finally {
          mutatingIds = []
          batchMutating = false
        }
      },
    })
  }

  const mutateSelectedCustomers = async (rows: DataRow[], clearSelection: () => void) => {
    const customerRows = rows as CustomerRow[]
    const archivedCount = customerRows.filter((row) => Boolean(row.customer?.archived_at)).length

    if (archivedCount > 0 && archivedCount < customerRows.length) {
      newNotification({
        class: "info",
        html: "Aktive und archivierte Ansprechpartner bitte getrennt auswählen.",
      })

      return
    }

    await mutateCustomers(
      selectedCustomerIds(rows),
      archivedCount === customerRows.length ? "restore" : "archive",
      clearSelection,
    )
  }

  type AssistEventPayload = {
    name?: string
    normalizedName?: string
    parameters?: Record<string, unknown>
  }

  type CustomerRegistryChangedPayload = {
    organizationId?: string | null
    organization_id?: string | null
    entity?: string | null
  }

  const matchesOrganization = (payload: AssistEventPayload) => {
    const params = payload.parameters ?? {}
    const targetOrg =
      (params.organization_id as string | undefined) ??
      (params.organizationId as string | undefined) ??
      (params.org_id as string | undefined)

    if (targetOrg && organization?.id && targetOrg !== organization.id) {
      return false
    }

    const normalized = (payload.normalizedName ?? payload.name ?? "").toLowerCase()

    return (
      normalized.includes("customer") ||
      normalized.includes("address") ||
      normalized.includes("contact")
    )
  }

  let unregisterAssist: (() => void) | null = null
  onMount(() => {
    const handlerId = `customers-${organization?.id ?? "org"}`
    const handler = (payload: AssistEventPayload) => {
      if (!matchesOrganization(payload)) return

      refreshTable?.()
    }
    registerEventCallback("assist.event", handlerId, handler)
    unregisterAssist = () => unregisterEventCallback("assist.event", handlerId)
  })
  onDestroy(() => unregisterAssist?.())

  let unregisterRegistryChange: (() => void) | null = null
  onMount(() => {
    const handlerId = `customers-registry-${organization?.id ?? "org"}`
    const handler = (payload: CustomerRegistryChangedPayload = {}) => {
      const targetOrg = payload.organizationId ?? payload.organization_id

      if (targetOrg && organization?.id && targetOrg !== organization.id) return

      refreshTable?.()
    }
    registerEventCallback("customer-registry.changed", handlerId, handler)
    unregisterRegistryChange = () => unregisterEventCallback("customer-registry.changed", handlerId)
  })
  onDestroy(() => unregisterRegistryChange?.())
</script>

<div class="customers-section">
  <CustomersTableHeader
    {organization}
    {canEdit}
    title={titleWithCount}
    {filterMode}
    onFilterModeChange={handleFilterModeChange}
    onCustomerCreated={() => {
      void refreshContactsTable()
    }}
  />

  <CustomersTableFilterBar
    bind:filters
    {filterMode}
    organizationId={organization?.id ?? ""}
    onChange={handleFilterChange}
  />

  <Table
    rowActions={{
      resolve: (row, rowIndex) =>
        buildTableRowActionSet(
          [
            {
              id: "open-record",
              label: "Ansprechpartner öffnen",
              icon: tablePrimaryIcon,
              onClick: () => openCustomer(row),
            },
            ...(((row) => {
              const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] = []

              if (canEdit) {
                const customerRow = row as CustomerRow

                actions.push(
                  ...CustomerRowActions({
                    row: customerRow,
                    mutating: mutatingIds.includes((customerRow.id as string) ?? ""),
                    onMutate: mutateCustomer,
                  }),
                )
              }

              return actions
            })(row) ?? []),
          ],
          {
            primaryId: "open-record",
            inlineIds: (row as CustomerRow).customer?.archived_at ? ["action-0"] : [],
          },
        ),
    }}
    columns={customerTableColumns}
    {getRows}
    setRefreshFunction={(fn) => (refreshTable = fn)}
    fetchDebounceMs={200}
    refreshKey={customerFilterRefreshKey}
    pageSize={PAGE_SIZE}
    theme="light"
    mode="table-scroll"
    urlStateId={customerTableUrlStateId}
    invalidationTargets={tableInvalidationTargets}
    showActions={canEdit}
    actionsHeaderLabel="Aktionen"
    enableSelection={canEdit}
    onRowsLoaded={(result) => {
      totalCount = result.total ?? 0
    }}
  >
    {#snippet tableCell({ row, column })}
      {@const customerRow = row as CustomerRow}
      {#if column.id === "identityLabel"}
        <CustomerIdentityCell row={customerRow} />
      {:else if column.id === "contactValue"}
        <CustomerContactCell row={customerRow} />
      {:else if column.id === "roleValues"}
        <CustomerRoleCell row={customerRow} />
      {:else if column.id === "objectEntries"}
        <CustomerObjectCell row={customerRow} />
      {:else}
        {row[column.id] ?? ""}
      {/if}
    {/snippet}

    {#snippet empty()}
      <div class="empty-state">
        <p style="color: var(--text-invers-100)"
          >Keine Ansprechpartner gefunden. Lege jetzt den ersten Ansprechpartner an.</p
        >
      </div>
    {/snippet}

    {#snippet batchActions({ selectedRows, clearSelection })}
      <CustomerBatchActions
        {selectedRows}
        {clearSelection}
        {batchMutating}
        onMutateSelected={mutateSelectedCustomers}
      />
    {/snippet}
  </Table>
</div>

<style lang="less">
  .empty-state {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0.6rem 0;

    p {
      margin: 0;
      color: color-mix(in srgb, var(--text-100) 70%, transparent);
    }
  }
</style>
